import { useState, useEffect, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_PRESENCE_PAGE_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { SPAViewContext } from '@confluence/spa-view-context';
import { useIsProductAdmin } from '@confluence/current-user';
import { useAccessStatus } from '@confluence/session-data';

import {
	type PresenceChangeboardingFeatureResponse,
	type PresenceChangeboardingFeatureProps,
	ChangeboardingState,
} from './PresenceChangeboarding';
import image from './assets/images/presence-spotlight-img.png';

const i18n = defineMessages({
	spotlightTitle: {
		id: 'team-presence.avatar-group.spotlight.title',
		defaultMessage:
			'{contentType, select, page {See who’s on your page} blogpost {See who’s on your blog post} other {See who’s on your content}}',
		description:
			'Title for a changeboarding spotlight that appears when user first enters a page to teach them how to see who is viewing or editing the page. {contentType} is the contents type',
	},
	spotlightDescriptionForUsers: {
		id: 'team-presence.avatar-group.spotlight.description-user',
		defaultMessage:
			'{contentType, select, page {You can now see who’s viewing this page in real time.} blogpost {You can now see who’s viewing this blog post in real time.} other {You can now see who’s viewing this content in real time.}}',
		description:
			'Description for a changeboarding spotlight shown to users that appears when user first enters a page to teach them how to see who is viewing or editing the page. {contentType} is the contents type',
	},
	spotlightDescriptionForAdmin: {
		id: 'team-presence.avatar-group.spotlight.description-admin',
		defaultMessage:
			'{contentType, select, page {Your team can now see who’s viewing this page in real time.} blogpost {Your team can now see who’s viewing this blog post in real time.} other {Your team can now see who’s viewing this content in real time.}}',
		description:
			'Description for a changeboarding spotlight that appears when user first enters a page to teach them how to see who is viewing or editing the page. {contentType} is the contents type',
	},
	spotlightAccessibleLabel: {
		id: 'team-presence.avatar-group.spotlight.label',
		defaultMessage: 'Onboarding for real-time presence avatar group',
		description:
			'Accessible label for a changeboarding spotlight that appears when user first enters a page to teach them how to see who is viewing or editing the page',
	},
	spotlightPrimaryAction: {
		id: 'team-presence.avatar-group.spotlight.primary-action',
		defaultMessage: 'OK',
		description:
			'Button text for a primary action on a changeboarding spotlight to acknowledge that you read the spotlight',
	},
	spotlightSecondaryActionForUsers: {
		id: 'team-presence.avatar-group.spotlight.secondary-action-user',
		defaultMessage: 'More about visibility',
		description:
			'Button text for a secondary action on a changeboarding spotlight showed to licensed users to learn more about real-time presence feature',
	},
	spotlightSecondaryActionForAdmin: {
		id: 'team-presence.avatar-group.spotlight.secondary-action-admin',
		defaultMessage: 'More about settings',
		description:
			'Button text for a secondary action on a changeboarding spotlight showed to admins to learn more about real-time presence feature',
	},
});

const FEATURE_NAME = 'presence';
// TODO: This must be the dynamic analytics source
// https://product-fabric.atlassian.net/browse/MDS-1330
export const PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE = 'presenceAvatarGroup';
export const PRESENCE_LEARN_MORE_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/view-a-page/';

type UsePresenceChangeboarding = (
	args: PresenceChangeboardingFeatureProps,
) => PresenceChangeboardingFeatureResponse;

export const usePresenceChangeboarding: UsePresenceChangeboarding = ({
	contentId,
	contentMode,
	contentType,
	totalParticipants,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [state, setState] = useState<ChangeboardingState>(ChangeboardingState.INITIAL);
	const { setOnboardingState, error: setError } = useSetOnboardingState();
	const {
		data: getData,
		error: getError,
		loading: getLoading,
	} = useGetOnboardingState(
		ONBOARDING_PRESENCE_PAGE_STATE_KEYS.PRESENCE_AVATAR_GROUP_PAGE_SPOTLIGHT_SEEN,
	);
	const { pagePresenceAvatarGroupSpotlightSeen: isSeen } = deserializeState(getData);
	const { isProductAdmin } = useIsProductAdmin();
	const { isNewUser } = useContext(SPAViewContext);
	const { isLicensed, isGuest } = useAccessStatus();
	const hasMoreThanOneParticipant = Boolean(totalParticipants && totalParticipants > 1);

	useEffect(() => {
		if (getLoading) {
			return;
		}

		if (
			contentType !== 'whiteboard' &&
			contentMode === 'view' &&
			!isSeen &&
			!isNewUser &&
			(isLicensed || isGuest) &&
			(isProductAdmin || hasMoreThanOneParticipant)
		) {
			setState(ChangeboardingState.READY);
			return;
		}

		if (isSeen) {
			setState(ChangeboardingState.COMPLETED);
			return;
		}

		if (setError || getError) {
			setState(ChangeboardingState.ERROR);
		}
	}, [
		contentMode,
		contentType,
		getError,
		getLoading,
		hasMoreThanOneParticipant,
		isGuest,
		isLicensed,
		isNewUser,
		isProductAdmin,
		isSeen,
		setError,
	]);

	useEffect(() => {
		if (state === ChangeboardingState.ERROR) {
			// TODO: This should be a subexperience errpr
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE,
					actionSubject: 'PresenceAvatarGroup',
					action: 'pulse failed',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: FEATURE_NAME,
					},
				},
			}).fire();
		}
	}, [contentId, contentType, contentMode, state, createAnalyticsEvent]);

	const handleComplete = () => {
		setState(ChangeboardingState.COMPLETED);
		void setOnboardingState({
			key: ONBOARDING_PRESENCE_PAGE_STATE_KEYS.PRESENCE_AVATAR_GROUP_PAGE_SPOTLIGHT_SEEN,
			value: 'true',
		});
	};

	const handleClose = () => {
		handleComplete();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE}Spotlight`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'presenceSpotlightClose',
				attributes: {
					isProductAdmin,
				},
			},
		}).fire();
	};

	const handleLearnMore = () => {
		handleComplete();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE}Spotlight`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'presenceSpotlightLearnMore',
				attributes: {
					isProductAdmin,
				},
			},
		}).fire();
	};

	const handlePulseClick = () => {
		setState(ChangeboardingState.ACTIVE);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${PRESENCE_AVATAR_GROUP_ANALYTIC_SOURCE}Pulse`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'presencePulse',
				attributes: {
					isProductAdmin,
				},
			},
		}).fire();
	};

	const heading = formatMessage(i18n.spotlightTitle, { contentType });
	const label = formatMessage(i18n.spotlightAccessibleLabel);
	const descriptionI18n = isProductAdmin
		? i18n.spotlightDescriptionForAdmin
		: i18n.spotlightDescriptionForUsers;
	const description = formatMessage(descriptionI18n, { contentType });
	const actions = [
		{
			onClick: handleClose,
			text: formatMessage(i18n.spotlightPrimaryAction),
			testId: 'presence-spotlight-action-ok',
		},
		{
			onClick: handleLearnMore,
			text: isProductAdmin
				? formatMessage(i18n.spotlightSecondaryActionForAdmin)
				: formatMessage(i18n.spotlightSecondaryActionForUsers),
			testId: 'presence-spotlight-action-learn-more',
			href: PRESENCE_LEARN_MORE_LINK,
			target: '_blank',
		},
	];

	return {
		featureName: FEATURE_NAME,
		state,
		content: {
			image,
			actions,
			heading,
			label,
			description,
			onPulseClick: handlePulseClick,
		},
	};
};
